import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToHash } from "@/lib/api";

const PRODUCT_LINKS = [
  { label: "AI & GPU Compute", href: "/products/gpu-ai-compute" },
  { label: "Servers & Workstations", href: "/products/servers-workstations" },
  { label: "Processors", href: "/products/processors" },
  { label: "Memory", href: "/products/memory" },
  { label: "Storage", href: "/products/storage" },
  { label: "Networking", href: "/products/networking" },
  { label: "Computer Components", href: "/products/computer-components" },
  { label: "Consumer Technology", href: "/products/consumer-technology" },
];

const LINKS = [
  { label: "Home", href: "/" },
  { label: "Products", href: "/products", dropdown: PRODUCT_LINKS },
  { label: "Supply", href: "/supply" },
  { label: "Company", href: "/company" },
  { label: "Contact", href: "/contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (href) => (e) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      e.preventDefault();
      const hash = href.slice(1);
      if (pathname === "/") scrollToHash(hash);
      else {
        navigate("/");
        setTimeout(() => scrollToHash(hash), 350);
      }
    }
  };

  const NavItem = ({ item }) => {
    if (!item.dropdown) {
      return item.href.startsWith("/#") ? (
        <a href={item.href} onClick={go(item.href)} data-testid={`nav-${item.label.toLowerCase()}`} className="px-4 py-2 text-sm font-medium text-midnight/70 hover:text-midnight transition-colors">
          {item.label}
        </a>
      ) : (
        <Link to={item.href} data-testid={`nav-${item.label.toLowerCase()}`} className="px-4 py-2 text-sm font-medium text-midnight/70 hover:text-midnight transition-colors">
          {item.label}
        </Link>
      );
    }
    return (
      <div className="relative group">
        <Link
          to={item.href}
          data-testid="nav-products"
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-midnight/70 hover:text-midnight transition-colors"
        >
          {item.label}
          <ChevronDown size={13} className="text-gold group-hover:rotate-180 transition-transform duration-300" />
        </Link>
        <div className="absolute left-0 top-full pt-2 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-300">
          <div className="bg-white hairline shadow-medium min-w-[240px] py-2">
            {item.dropdown.map((d) => (
              <Link
                key={d.label}
                to={d.href}
                data-testid={`nav-product-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="block px-5 py-2.5 text-sm text-midnight/65 hover:text-gold hover:bg-[#FAFAFA] transition-colors"
              >
                {d.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass" data-testid="main-nav">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" aria-label="Royal Deals FZ-LLC home"><Logo /></Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => <NavItem key={l.label} item={l} />)}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/request"
            data-testid="nav-request-hardware"
            className="hidden sm:inline-block bg-midnight text-white text-sm font-medium px-6 py-3 hover:bg-midnight-light transition-colors"
          >
            Request Hardware
          </Link>
          <button
            className="lg:hidden text-midnight"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b border-midnight/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6">
              {LINKS.map((item) => (
                <div key={item.label}>
                  {item.href.startsWith("/#") ? (
                    <a href={item.href} onClick={go(item.href)} data-testid={`mobile-nav-${item.label.toLowerCase()}`} className="block py-3 text-base font-medium text-midnight/80 hover:text-gold transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${item.label.toLowerCase()}`} className="block py-3 text-base font-medium text-midnight/80 hover:text-gold transition-colors">
                      {item.label}
                    </Link>
                  )}
                  {item.dropdown && (
                    <div className="pl-4 pb-2">
                      {item.dropdown.map((d) => (
                        <Link key={d.label} to={d.href} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-midnight/55">
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/request"
                onClick={() => setOpen(false)}
                data-testid="mobile-request-hardware"
                className="mt-4 block text-center bg-midnight text-white text-sm font-medium px-6 py-3.5"
              >
                Request Hardware
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
